import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Play, CheckCircle2, FileText, Sparkles, Lock, 
  Loader2, Send, Eye, EyeOff, ChevronRight, MessageCircle,
  AlertTriangle, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import ReactMarkdown from 'react-markdown';
import { useAuth } from 'contexts/AuthContext';

const CoursePlayer = () => {
  const { moduleId, courseId } = useParams();
  const { api } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('transcript');
  const [focusMode, setFocusMode] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [notes, setNotes] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [currentProgress, setCurrentProgress] = useState(null);
  
  // --- NUEVOS ESTADOS ANTI-FRAUDE ---
  const [startTime, setStartTime] = useState(Date.now());
  const [fraudAlert, setFraudAlert] = useState(false);
  const videoRef = useRef(null);
  const chatEndRef = useRef(null);

  // 1. EFECTO GLOBAL ANTI-FRAUDE (Bloqueos físicos y detección de pestañas)
  useEffect(() => {
    const handlePrevention = (e) => {
      e.preventDefault();
      setFraudAlert(true);
      setTimeout(() => setFraudAlert(false), 3000);
      return false;
    };

    // Bloquear botón derecho, copiar y pegar
    document.addEventListener('contextmenu', handlePrevention);
    document.addEventListener('copy', handlePrevention);
    document.addEventListener('paste', handlePrevention);

    // Detectar si el usuario cambia de pestaña
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.warn("Pérdida de foco detectada.");
        if (videoRef.current) videoRef.current.pause();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('contextmenu', handlePrevention);
      document.removeEventListener('copy', handlePrevention);
      document.removeEventListener('paste', handlePrevention);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 2. SEGUIMIENTO DE VIDEO (Heartbeat cada 10 segundos)
  useEffect(() => {
    if (!videoRef.current) return;

    const heartbeatInterval = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        sendHeartbeat(videoRef.current.currentTime, videoRef.current.duration);
      }
    }, 10000);

    return () => clearInterval(heartbeatInterval);
  }, [course]);

  const sendHeartbeat = async (currentTime, duration) => {
    try {
      await api.post(
        `/modules/${moduleId}/video/${courseId}/heartbeat`,
        null,
        { params: { current_time: Math.floor(currentTime), duration: Math.floor(duration) } }
      );
    } catch (error) {
      console.error("Error heartbeat:", error);
    }
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : url;
  };

  const toggleFocusMode = () => setFocusMode(!focusMode);

  // Simulación de envío de mensaje (ajustar según tu API)
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', content: chatInput };
    setChatMessages([...chatMessages, userMsg]);
    setChatInput('');
    setChatLoading(true);
    // Lógica de API aquí...
    setChatLoading(false);
  };

  if (!course && loading) return <div className="h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-lime-500" /></div>;
  if (!course) return <div className="h-screen bg-black flex items-center justify-center text-white">Curso no encontrado</div>;

  return (
    <div className="h-[calc(100vh-64px)] flex bg-black relative" data-testid="course-player">
      
      {/* AVISO ANTI-FRAUDE FLOTANTE */}
      {fraudAlert && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-bounce">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-bold text-sm uppercase tracking-tighter">Acción bloqueada: Escribe tu respuesta manualmente</span>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-500 ${sidebarOpen ? 'mr-96' : ''}`}>
        
        {/* Top Bar */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0a0a0a]">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild className="hover:bg-white/5 text-white/70">
              <Link to={`/skill-paths/${course.skill_path_id}`}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
              </Link>
            </Button>
            <div>
              <h1 className="font-bold text-white tracking-tight">{course.title}</h1>
              <p className="text-xs text-white/40 uppercase font-bold tracking-widest">{course.duration_minutes} MINUTOS</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10 mr-4">
              <ShieldCheck className="w-3 h-3 text-lime-500" />
              <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">Protección Activa</span>
            </div>
            <Button variant="ghost" size="sm" onClick={toggleFocusMode} className="text-white/60">
              {focusMode ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline text-[10px] font-black uppercase tracking-widest">
                {focusMode ? 'Normal' : 'Enfoque'}
              </span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/60">
              {sidebarOpen ? <ChevronRight className="w-4 h-4" /> : <MessageCircle className="w-4 h-4 text-lime-500" />}
            </Button>
          </div>
        </div>

        {/* Video Player */}
        <div className="flex-1 bg-[#050505] flex items-center justify-center relative overflow-hidden">
          {course.video_url ? (
            course.video_type === 'youtube' ? (
              <iframe 
                src={`${getYouTubeEmbedUrl(course.video_url)}?enablejsapi=1`} 
                className="w-full h-full" 
                allowFullScreen 
              />
            ) : (
              <video 
                ref={videoRef} 
                src={course.video_url} 
                className="w-full h-full" 
                controls 
                onTimeUpdate={handleVideoProgress}
                onPlay={() => setStartTime(Date.now())}
                controlsList="nodownload" // Evita descarga
              />
            )
          ) : (
            <div className="text-center">
              <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/10">
                <Play className="w-8 h-8 text-white/20" />
              </div>
              <p className="text-white/40 font-bold uppercase text-xs tracking-widest">Material de lectura únicamente</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-[#0a0a0a] border-t border-white/10 flex items-center justify-between">
          <div className="flex-1 max-w-md mr-8">
            <div className="flex justify-between text-[10px] font-black text-white/30 uppercase mb-2">
              <span>Progreso de visualización real</span>
              <span>{Math.round(videoProgress)}%</span>
            </div>
            <Progress value={videoProgress} className="h-1 bg-white/5" indicatorClassName="bg-lime-500" />
          </div>
          
          <div className="flex items-center gap-4">
            {currentProgress?.completed ? (
              <Badge className="bg-lime-500/20 text-lime-500 border-lime-500/30 px-4 py-2 rounded-xl font-bold">
                <CheckCircle2 className="w-4 h-4 mr-2" /> COMPLETADO
              </Badge>
            ) : (
              <Button 
                disabled={videoProgress < 95}
                className="bg-lime-500 text-black font-black uppercase hover:bg-lime-400 px-8 rounded-xl shadow-lg shadow-lime-500/10 disabled:opacity-30"
              >
                {videoProgress < 95 ? `Mira el ${95 - Math.round(videoProgress)}% restante` : 'Finalizar Lección'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar - Tutor IA y Transcripción */}
      <div 
        className={`fixed right-0 top-16 bottom-0 w-96 bg-[#0a0a0a] border-l border-white/10 transition-transform duration-500 z-20 ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className="w-full justify-start rounded-none border-b border-white/5 h-14 bg-black px-2 gap-2">
            <TabsTrigger value="transcript" className="data-[state=active]:bg-white/5 data-[state=active]:text-white font-bold text-xs uppercase tracking-widest">
              <FileText className="w-3 h-3 mr-2" /> Transcripción
            </TabsTrigger>
            <TabsTrigger 
              value="chat" 
              className={`font-bold text-xs uppercase tracking-widest ${aiEnabled ? 'text-lime-500' : 'opacity-30 cursor-not-allowed'}`}
              disabled={!aiEnabled}
            >
              {aiEnabled ? <Sparkles className="w-3 h-3 mr-2 animate-pulse" /> : <Lock className="w-3 h-3 mr-2" />}
              Tutor IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="transcript" className="flex-1 m-0 p-0 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-6">
                <h3 className="text-white font-bold text-lg tracking-tight">Contenido Detallado</h3>
                <div className="text-sm text-white/60 leading-relaxed space-y-4">
                  <ReactMarkdown>{course.transcript || course.content || 'Sin transcripción.'}</ReactMarkdown>
                </div>
              </div>
            </ScrollArea>
            <div className="p-6 border-t border-white/5 bg-black/40">
              <Label className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-3 block">Respuesta del Juego de Rol</Label>
              <Textarea
                placeholder="Escribe tu respuesta manualmente aquí..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                
                // --- BLOQUEOS ANTI-FRAUDE DIRECTOS ---
                onPaste={(e) => {
                  e.preventDefault();
                  setFraudAlert(true);
                  setTimeout(() => setFraudAlert(false), 3000);
                }}
                onCopy={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                
                className="bg-white/5 border-white/10 text-white min-h-[150px] rounded-xl focus:border-lime-500 transition-all resize-none"
              />
              <p className="text-[9px] text-white/20 mt-2 uppercase font-bold">La IA analizará tu velocidad de escritura y contenido para validar la lección.</p>
            </div>
          </TabsContent>

          <TabsContent value="chat" className="flex-1 m-0 p-0 flex flex-col bg-[#050505] overflow-hidden">
            {aiEnabled ? (
              <>
                <ScrollArea className="flex-1 p-6">
                  <div className="space-y-6 pb-4">
                    {chatMessages.length === 0 && (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-lime-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-lime-500/20">
                          <Sparkles className="text-lime-500 w-8 h-8" />
                        </div>
                        <h3 className="text-white font-black uppercase text-sm tracking-tighter mb-2">Asistente Cognitivo</h3>
                        <p className="text-white/40 text-xs px-8 leading-relaxed">
                          Tengo acceso a la transcripción de esta lección. ¿Hay algo que no te haya quedado claro?
                        </p>
                      </div>
                    )}
                    {chatMessages.map((msg, index) => (
                      <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[90%] p-4 rounded-2xl text-sm leading-relaxed ${
                          msg.role === 'user' 
                            ? 'bg-lime-500 text-black font-bold rounded-tr-none' 
                            : 'bg-white/5 text-white/80 border border-white/10 rounded-tl-none'
                        }`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-white/5 p-4 rounded-2xl border border-white/10">
                          <Loader2 className="w-4 h-4 animate-spin text-lime-500" />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                
                <div className="p-6 bg-black border-t border-white/10">
                  <div className="flex gap-2 bg-white/5 p-2 rounded-2xl border border-white/10 focus-within:border-lime-500 transition-all">
                    <Textarea
                      placeholder="Escribe tu duda..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendChatMessage())}
                      className="min-h-[44px] max-h-32 bg-transparent border-none focus-visible:ring-0 text-white text-sm resize-none scrollbar-hide"
                    />
                    <Button onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()} className="bg-lime-500 text-black hover:bg-lime-400 h-10 w-10 p-0 rounded-xl">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                <Lock className="w-12 h-12 text-white/10 mb-6" />
                <h3 className="text-white font-bold uppercase tracking-widest text-xs mb-2">Tutor IA Desactivado</h3>
                <p className="text-white/30 text-[10px] leading-relaxed">
                  Tu administrador de organización debe habilitar las funciones de IA para tu perfil.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CoursePlayer;